/* ==========================================================================
   FurrFlora: cookie choices
   --------------------------------------------------------------------------
   Shows a banner on the first visit: Accept all / Essential only / Choose.
   The answer is remembered on the visitor's device. Analytics and marketing
   scripts listed below are only loaded AFTER the visitor has said yes to that
   category, and never before. "Cookie settings" in the footer reopens it.

   To add a tracker later, put its script address in CONSENT_CONFIG.scripts,
   e.g. analytics: ['https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX'].
   Never paste tracking snippets straight into the HTML: they would run
   without consent.
   ========================================================================== */
const CONSENT_CONFIG = {
  version: 1,                     // raise this to ask everyone again (e.g. after adding a new kind of tracker)
  policyUrl: '/?view=info#cookies', // "what we store" section
  scripts: {
    analytics: [],                // e.g. Google Analytics
    marketing: [],                // e.g. Meta / TikTok pixels, Klaviyo onsite tracking
  },
};

(() => {
  const KEY = 'furrflora-consent';
  const CATEGORIES = ['analytics', 'marketing'];
  const store = {
    get: () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } },
    set: (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* private mode: asked again next visit */ } },
  };

  // Google Consent Mode: everything is denied until the visitor chooses (harmless if Google tags are never added)
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  gtag('consent', 'default', { ad_storage: 'denied', analytics_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });

  let choice = store.get();
  if (choice && choice.v !== CONSENT_CONFIG.version) choice = null; // ask again after a policy change
  const listeners = [];
  const loaded = new Set();

  // On the Shopify store: pass the same answer to Shopify (its Customer Privacy API), so Shopify's own
  // analytics and pixels follow it and Shopify's built-in cookie banner can stay switched off (2026-10-21)
  const tellShopify = (c) => {
    const S = window.Shopify;
    if (!S || typeof S.loadFeatures !== 'function') return; // the plain website: nothing to tell
    const send = () => {
      const cp = S.customerPrivacy;
      if (!cp || typeof cp.setTrackingConsent !== 'function') return;
      const want = { analytics: c.analytics, marketing: c.marketing, preferences: c.analytics || c.marketing, sale_of_data: c.marketing };
      const now = typeof cp.currentVisitorConsent === 'function' ? cp.currentVisitorConsent() || {} : {};
      if (Object.keys(want).every((k) => now[k] === (want[k] ? 'yes' : 'no'))) return; // already up to date
      try { cp.setTrackingConsent(want, () => {}); } catch { /* Shopify's problem, not the shopper's */ }
    };
    if (S.customerPrivacy) send();
    else S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => { if (!err) send(); });
  };

  // load the scripts for the categories the visitor has allowed (once each)
  const apply = () => {
    const c = choice || {};
    if (choice) tellShopify(choice);
    gtag('consent', 'update', {
      analytics_storage: c.analytics ? 'granted' : 'denied',
      ad_storage: c.marketing ? 'granted' : 'denied',
      ad_user_data: c.marketing ? 'granted' : 'denied',
      ad_personalization: c.marketing ? 'granted' : 'denied',
    });
    CATEGORIES.forEach((cat) => {
      if (!c[cat]) return;
      (CONSENT_CONFIG.scripts[cat] || []).forEach((src) => {
        if (loaded.has(src)) return;
        loaded.add(src);
        const s = document.createElement('script');
        s.src = src; s.async = true;
        document.head.appendChild(s);
      });
    });
    listeners.forEach((fn) => { try { fn({ ...c }); } catch { /* a listener's problem, not ours */ } });
    document.dispatchEvent(new CustomEvent('ff:consent', { detail: { ...c } }));
  };

  // for other scripts: window.ffConsent.has('analytics'), .onChange(fn), .open()
  window.ffConsent = {
    has: (cat) => Boolean(choice && choice[cat]),
    get: () => (choice ? { ...choice } : null),
    onChange: (fn) => { listeners.push(fn); },
    open: () => openBanner(true),
  };

  let el = null;
  let lastFocus = null;

  const save = (analytics, marketing) => {
    const wasOn = choice ? CATEGORIES.filter((k) => choice[k]) : [];
    choice = { v: CONSENT_CONFIG.version, analytics: Boolean(analytics), marketing: Boolean(marketing), at: new Date().toISOString() };
    store.set(choice);
    closeBanner();
    // turning a category off can't unload a script that already ran, so start the page afresh
    if (wasOn.some((k) => !choice[k] && (CONSENT_CONFIG.scripts[k] || []).length)) { location.reload(); return; }
    apply();
  };

  const closeBanner = () => {
    if (!el) return;
    el.classList.remove('is-open');
    const node = el; el = null;
    setTimeout(() => node.remove(), 300);
    if (lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  };

  function openBanner(showChoices) {
    if (el) { if (showChoices) el.classList.add('show-choices'); return; }
    lastFocus = showChoices ? document.activeElement : null;
    el = document.createElement('section');
    el.className = 'cookie-banner' + (showChoices ? ' show-choices' : '');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'false');
    el.setAttribute('aria-labelledby', 'cookie-title');
    const c = choice || {};
    el.innerHTML = `
      <div class="cookie-main">
        <h2 class="cookie-title" id="cookie-title">Your cookie choices</h2>
        <p class="cookie-text">We use essential cookies to make the shop work, like remembering your cart. With your OK, we'd also use cookies to see how the site is used and to show you relevant offers. <a href="${CONSENT_CONFIG.policyUrl}">What we store</a></p>
        <div class="cookie-choices" data-cookie-choices>
          <label class="cookie-row is-locked">
            <span><strong>Essential</strong><small>Your cart, your Cat or Dog choice and this answer. Always on.</small></span>
            <input type="checkbox" checked disabled aria-label="Essential cookies, always on">
          </label>
          <label class="cookie-row">
            <span><strong>Analytics</strong><small>Helps us see which pages are useful, without knowing who you are.</small></span>
            <input type="checkbox" role="switch" data-cookie-cat="analytics" ${c.analytics ? 'checked' : ''}>
          </label>
          <label class="cookie-row">
            <span><strong>Marketing</strong><small>Lets us show you FurrFlora offers on other sites.</small></span>
            <input type="checkbox" role="switch" data-cookie-cat="marketing" ${c.marketing ? 'checked' : ''}>
          </label>
        </div>
      </div>
      <div class="cookie-actions">
        <button type="button" class="cookie-btn" data-cookie="all">Accept all</button>
        <button type="button" class="cookie-btn" data-cookie="none">Essential only</button>
        <button type="button" class="cookie-btn is-quiet" data-cookie="choose">Choose</button>
        <button type="button" class="cookie-btn" data-cookie="save">Save my choices</button>
      </div>`;
    el.addEventListener('click', (e) => {
      const b = e.target.closest('[data-cookie]');
      if (!b) return;
      const act = b.dataset.cookie;
      if (act === 'all') save(true, true);
      else if (act === 'none') save(false, false);
      else if (act === 'choose') { el.classList.add('show-choices'); el.querySelector('[data-cookie-cat]').focus(); }
      else if (act === 'save') save(el.querySelector('[data-cookie-cat="analytics"]').checked, el.querySelector('[data-cookie-cat="marketing"]').checked);
    });
    el.addEventListener('keydown', (e) => { if (e.key === 'Escape' && choice) closeBanner(); }); // Esc only once a choice exists
    document.body.appendChild(el);
    void el.offsetWidth; // apply the start styles first so the slide-in runs
    el.classList.add('is-open');
    if (showChoices) el.querySelector('[data-cookie-cat]').focus({ preventScroll: true });
  }

  // "Cookie settings" links anywhere on the site reopen the choices
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cookie-settings]')) { e.preventDefault(); openBanner(true); }
  });

  if (choice) apply();
  else {
    const show = () => setTimeout(() => openBanner(false), 500);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', show, { once: true }); else show();
  }
})();
